import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { generateClient } from "aws-amplify/api";
import {
  History,
  Search,
  Sparkles,
  Swords,
  Gamepad2,
  Trophy,
  User,
  Users,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  listEvents,
  listEventPlayerResults,
  listEventTopCutResults,
  listLeagues,
  listLeaguePointAdjustments,
  listPlayerConstructs,
} from "../graphql/queries";
import { getLeagueGame, LEAGUE_GAMES, LeagueGame } from "../lib/leagueGames";
import { getCurrentLeague, getLeagueStatus, League } from "../lib/leagueUtils";
import {
  aggregateLeaguePlayerStats,
  calculateWinRate,
} from "../lib/playerStats";

const client = generateClient();

const LeaguePlayers: React.FC = () => {
  const [selectedGame, setSelectedGame] = useState<LeagueGame>(
    "MAGIC_THE_GATHERING"
  );
  const [searchTerm, setSearchTerm] = useState("");

  const { data: leagues, isLoading: leaguesLoading } = useQuery({
    queryKey: ["leagues"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listLeagues,
        variables: { limit: 100 },
      });
      return result.data.listLeagues.items;
    },
  });
  const { data: events, isLoading: eventsLoading } = useQuery({
    queryKey: ["allEvents"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEvents,
        variables: { limit: 1000 },
      });
      return result.data.listEvents.items;
    },
  });
  const { data: results, isLoading: resultsLoading } = useQuery({
    queryKey: ["allEventPlayerResults"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventPlayerResults,
        variables: { limit: 1000 },
      });
      return result.data.listEventPlayerResults.items;
    },
  });
  const { data: topCutResults, isLoading: topCutLoading } = useQuery({
    queryKey: ["allTopCutResults"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventTopCutResults,
        variables: { limit: 1000 },
      });
      return result.data.listEventTopCutResults.items;
    },
  });
  const { data: constructs, isLoading: constructsLoading } = useQuery({
    queryKey: ["allPlayerConstructs"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });
      return result.data.listPlayerConstructs.items;
    },
  });
  const { data: adjustments, isLoading: adjustmentsLoading } = useQuery({
    queryKey: ["allLeaguePointAdjustments"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listLeaguePointAdjustments,
        variables: { limit: 1000 },
      });
      return result.data.listLeaguePointAdjustments.items;
    },
  });

  const activeLeagueByGame = useMemo(
    () =>
      (Object.keys(LEAGUE_GAMES) as LeagueGame[]).reduce((map, game) => {
        map[game] = getCurrentLeague(
          ((leagues || []) as League[]).filter(
            (league) =>
              getLeagueGame(league.game) === game &&
              getLeagueStatus(league).status === "running"
          )
        );
        return map;
      }, {} as Record<LeagueGame, League | null>),
    [leagues]
  );
  const activeLeague = activeLeagueByGame[selectedGame];
  const summaries = useMemo(
    () =>
      activeLeague
        ? aggregateLeaguePlayerStats(
            activeLeague,
            events || [],
            results || [],
            topCutResults || [],
            adjustments || []
          )
        : [],
    [activeLeague, adjustments, events, results, topCutResults]
  );
  const players = useMemo(
    () =>
      summaries
        .map((summary) => ({
          ...summary,
          construct: constructs?.find(
            (construct: any) => construct.id === summary.playerConstructId
          ),
        }))
        .filter((player) =>
          (player.construct?.name || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        ),
    [constructs, searchTerm, summaries]
  );
  const isLoading =
    leaguesLoading ||
    eventsLoading ||
    resultsLoading ||
    topCutLoading ||
    constructsLoading ||
    adjustmentsLoading;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
            <Users className="h-8 w-8 text-primary" />
            Active League Players
          </h1>
          <p className="text-muted-foreground">
            League-specific player records, placement, and finish percentile
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/players/history">
            <History className="mr-2 h-4 w-4" />
            Career History
          </Link>
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3" role="tablist">
        {(Object.keys(LEAGUE_GAMES) as LeagueGame[]).map((game) => {
          const { label } = LEAGUE_GAMES[game];
          const Icon =
            game === "MAGIC_THE_GATHERING"
              ? Sparkles
              : game === "RIFTBOUND"
              ? Swords
              : Gamepad2;
          const league = activeLeagueByGame[game];
          return (
            <Button
              key={game}
              role="tab"
              aria-selected={selectedGame === game}
              variant={selectedGame === game ? "default" : "outline"}
              className="h-auto justify-start px-4 py-4"
              disabled={!league}
              onClick={() => setSelectedGame(game)}
            >
              <Icon className="mr-3 h-5 w-5" />
              <span className="text-left">
                <span className="block font-semibold">{label}</span>
                <span className="block text-xs opacity-75">
                  {league?.name || "No active league"}
                </span>
              </span>
            </Button>
          );
        })}
      </div>

      {activeLeague && (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
            <div>
              <p className="font-semibold">{activeLeague.name}</p>
              <p className="text-sm text-muted-foreground">
                {activeLeague.format} · {activeLeague.startDate} to{" "}
                {activeLeague.endDate || "ongoing"}
              </p>
            </div>
            <Badge>{players.length} players</Badge>
          </CardContent>
        </Card>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-10"
          placeholder="Search this league..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="flex min-h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      ) : players.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No player results are available for this active league yet.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {players.map((player) => (
            <Card key={player.playerConstructId}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                      <User className="h-5 w-5 text-primary" />
                    </span>
                    <div>
                      <CardTitle className="text-lg">
                        {player.construct?.name || "Unknown player"}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        Rank #{player.currentRank}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary">{player.totalPoints} pts</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Average placement</p>
                    <p className="font-semibold">
                      {player.averageRank.toFixed(1)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Finish percentile</p>
                    <p className="font-semibold">
                      {player.averageFinishPercentile.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Events</p>
                    <p className="font-semibold">{player.totalEvents}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Record</p>
                    <p className="font-semibold">
                      {player.totalWins}-{player.totalLosses}-
                      {player.totalDraws}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">
                      {activeLeague?.format === "Commander"
                        ? "Pod win rate"
                        : "League win rate"}
                    </p>
                    <p className="font-semibold">
                      {calculateWinRate(
                        player.totalWins,
                        player.totalLosses,
                        player.totalDraws
                      ).toFixed(1)}
                      %
                    </p>
                  </div>
                </div>
                <Button asChild variant="outline" className="w-full">
                  <Link to={`/players/${player.playerConstructId}/history`}>
                    <Trophy className="mr-2 h-4 w-4" />
                    View Career
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeaguePlayers;
